import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, Gift } from "lucide-react";
import Icon from "../Icon";
import { AvatarWithProgress } from "../AvatarWithProgress";
import LanguageDropdown from "../LanguageDropdown";
import { WalletBalance } from "../WalletBalance";
import LogoutModal from "../modals/LogoutModal";
import { useAppContext } from "@/context/AppContext";
import CoinSkeleton from "@/skeleton-1/CoinSkeleton"
const Sidebar = ({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  user,
  navigation,
  unreadCount,
  handleMarkAllAsRead,
  handleLogout,
  balance,
  showPackage,
  setShowPackage,
  logoText,
  LogoWhiteText,
  t,
  handleReferShareClick,
  planMsgOpen,
  setPlanMsgOpen,
  coinsMsgOpen,
  setCoinsMsgOpen,
  referralMsgOpen,
  setReferralMsgOpen,
  openManageSubscription,
  userMenuRef,
  packageDropdownRef,
  planRef,
  coinsRef,
  referralRef,
  zoom,
}: any) => {
  const location = useLocation();
  const [isIOS, setIsIOS] = React.useState(false);
  const [scrollPosition, setScrollPosition] = React.useState(0);
  const [isAndroid, setIsAndroid] = React.useState(false);
  const { loader } = useAppContext();
  React.useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    const android = /Android/.test(navigator.userAgent);
    setIsAndroid(android);
  }, []);

  // iPhone bottom bar को scroll down करने का function
  const hideIPhoneBottomBar = () => {
    if (!isIOS) return;

    // Method 1: Scroll down slightly to hide address bar
    window.scrollTo({
      top: 100,
      behavior: "instant" as any,
    });

    // Method 2: Force layout recalculation
    setTimeout(() => {
      document.body.style.height = "100%";
      document.body.style.overflow = "hidden";

      // Method 3: Focus and blur trick
      const tempInput = document.createElement("input");
      tempInput.style.position = "absolute";
      tempInput.style.opacity = "0";
      tempInput.style.height = "0";
      document.body.appendChild(tempInput);

      tempInput.focus();
      setTimeout(() => {
        tempInput.blur();
        document.body.removeChild(tempInput);

        // Final scroll to ensure UI hides
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "instant" as any,
        });
      }, 50);
    }, 100);
  };

  const handleSidebarOpen = () => {
    // Save current scroll position
    setScrollPosition(window.scrollY);

    // Open sidebar
    setIsMobileMenuOpen(true);

    // iOS पर bottom bar hide करें
    if (isIOS) {
      // Body को fixed करें ताकि scroll न हो
      document.body.style.position = "fixed";
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";

      // Small delay के बाद bottom bar hide करें
      setTimeout(() => {
        hideIPhoneBottomBar();
      }, 300); // Wait for sidebar animation
    }
  };

  const handleSidebarClose = () => {
    // Close sidebar
    setIsMobileMenuOpen(false);

    // iOS पर reset करें
    if (isIOS) {
      // Reset body styles
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.body.style.height = "";

      // Restore scroll position
      window.scrollTo(0, scrollPosition);
    }
  };

  // Navigation items के लिए close handler
  const handleNavItemClick = () => {
    handleSidebarClose();
  };
const totalCoins =
  user?.wallet?.coins + user?.wallet?.referralCoin;

const showSkeleton =
  loader && Number.isNaN(totalCoins);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  return (
    <div className="overflow-hidden">
      <div
        style={{
          zoom: zoom,
          ...(isIOS && { height: "100vh" }),
          // Platform-specific height handling
          ...(isIOS && {
            paddingBottom: "env(safe-area-inset-bottom)",
            height: "-webkit-fill-available",
          }),
          ...(isAndroid && {
            height: "100%", // Use 100% for Android instead of 100vh
            minHeight: "-webkit-fill-available",
          }),
        }}
        className={`fixed inset-y-0 left-0 z-50 w-full sm:w-2/5 xl:w-1/5 theme-bg-trinary border-r border-white/10 transform transition-transform duration-300 ease-in-out overflow-y-auto
          ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
          ${isIOS ? "ios-scroll" : ""}
          ${isAndroid ? "android-full-height" : ""}
        `}
      >
        {/* Top Logo */}
        <div className="flex items-center justify-between border-b border-white/20 p-2 py-3">
          <Link
            to="/dashboard"
            onClick={handleSidebarClose}
            className="flex items-center"
          >
            <Icon
              name="spiral-logo"
              className="ml-2 brightness-[200]"
              size={38}
            />
            <span className="theme-text-primary text-2xl lg:text-[1.6rem] tracking-tight ml-3">
              <img src={LogoWhiteText} alt="Logo" className="h-4" />
            </span>
          </Link>

          <button
            onClick={handleSidebarClose}
            className="rounded-md theme-text-light mr-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* User Profile */}
        <div className="border-b border-white/20 relative" ref={userMenuRef}>
          <Link
            to={"/dashboard?edit-profile=true"}
            onClick={handleSidebarClose}
            className="flex items-center gap-x-2 mb-0 w-full hover:theme-bg-secondary rounded-md p-2 transition-colors"
          >
            <div className="mx-2 py-4">
              <AvatarWithProgress user={user} className="w-10 h-10" size={55} />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-md font-medium theme-text-light truncate">
                {user?.profile?.fullName || user?.email || "User"}
              </div>
              <div className="text-sm theme-text-light truncate">
                {user?.email}
              </div>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-1 py-2.5 flex flex-col mx-2 gap-y-2">
          {navigation.map((item: any) => {
            const isActive = location.pathname === item.path;
            const IconComponent = item.icon;
            const showBadge = item.name === "History" && unreadCount > 0;
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`hover:bg-[#d7d7fc] hover:text-[#7650e3] hover:border-[#7650e3] flex items-center justify-between px-3 py-3 text-md font-medium rounded-md transition-colors min-h-[44px] ${
                  isActive
                    ? "theme-bg-primary theme-text-secondary"
                    : "theme-text-light"
                }`}
                onClick={handleNavItemClick}
              >
                <div className="flex justify-center capitalize items-center">
                  <IconComponent size={20} className="mr-3" />
                  {t(item.key)}
                </div>
                {showBadge && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleMarkAllAsRead();
                    }}
                    className="h-6 w-6 bg-purple-500 hover:bg-purple-600 rounded-full flex items-center justify-center text-xs text-white font-bold transition-colors duration-200 cursor-pointer border-0 outline-none"
                    title={`Mark all ${unreadCount} unread posts as read`}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </button>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="flex-1 px-1 hover:text-purple-600 flex flex-col rounded-md text-white mx-2 gap-y-2">
          <button
            onClick={() => {
              setIsMobileMenuOpen(false); // sidebar close
              setIsLogoutModalOpen(true); // modal open
            }}
            className="hover:bg-[#d7d7fc] hover:text-[#7650e3] hover:border-[#7650e3] flex items-center justify-between px-3 py-2.5 text-md font-medium rounded-md transition-colors"
          >
            <div className="flex justify-center items-center">
              <LogOut className="h-5 w-5 mr-3" />
              <div className="flex-1">
                <p className="font-medium">{t("logout")}</p>
              </div>
            </div>
          </button>
        </div>

        {/* Language Dropdown */}
        <div className="flex gap-2 mt-3 px-2.5 md:px-2.5">
          <LanguageDropdown
            showGlobe={true}
            alignRight={true}
            className="text-white"
          />
        </div>

        {/* Refer & Earn */}
        <div className="px-3 mt-4 mb-4">
          <button
            type="button"
            onClick={() => {
              handleReferShareClick();
              handleSidebarClose();
            }}
            className="w-full rounded-xl bg-transparent"
            title="Refer & Earn"
          >
            <div className="w-full flex items-center justify-between bg-white rounded-md px-4 py-3 border border-black/10 shadow-sm">
              <div className="text-left">
                <p className="text-lg font-semibold text-[#7650e3] leading-tight">
                  {t("refer_and_earn")}
                </p>
                <p className="text-sm text-gray-500 leading-tight mt-1">
                  {t("refer_and_earn_message")}
                </p>
              </div>
              <div className="ml-3 shrink-0 w-12 h-12 rounded-full bg-[#7650e3] flex items-center justify-center">
                <Gift className="w-6 h-6 text-white" />
              </div>
            </div>
          </button>
        </div>

        {/* Footer */}
        <div className="mt-auto border-t border-white/20 pt-4">
          <div className="text-center flex flex-col items-center justify-center gap-3 p-2">
            <div className="flex justify-center space-x-3 md:text-xs text-sm theme-text-light mb-1">
              <Link
                to="/privacy"
                className="transition-colors duration-200 underline"
                onClick={handleSidebarClose}
              >
                {t("privacy_policy")}
              </Link>
              <Link
                to="/terms"
                className="transition-colors duration-200 underline"
                onClick={handleSidebarClose}
              >
                {t("terms_service")}
              </Link>
              <Link
                to="/support"
                className="transition-colors duration-200 underline"
                onClick={handleSidebarClose}
              >
                {t("support")}
              </Link>
            </div>
            <div className="flex justify-center space-x-1 text-xs theme-text-light w-full">
              {/* social media links icons */}
              <div className="text-tertiary-foreground mt-2 flex flex-row justify-between gap-1.5 px-5 w-full">
                {/* Facebook - Official 2024 Circle Logo */}
                <a
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  href="https://www.facebook.com/share/1H74CpLTCK/"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="min-w-[20px] min-h-[20px] shrink-0"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12 0C5.373 0 0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073c0-6.627-5.373-12-12-12z"
                      fill="white"
                    />
                  </svg>
                </a>

                {/* Instagram - White Glyph */}
                <a
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  href="https://www.instagram.com/omnishare.ai/"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="min-w-[20px] min-h-[20px] shrink-0"
                  >
                    <path
                      d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                      fill="white"
                    />
                  </svg>
                </a>

                {/* LinkedIn - White Rounded Square with Cut-out 'in' */}
                <a
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  href="https://www.linkedin.com/company/omnishare-ai"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="min-w-[20px] min-h-[20px] shrink-0"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0 4a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v16a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4zm7.12 16.452H3.558V8.995H7.12v11.457zM5.34 7.433a2.064 2.064 0 1 1 0-4.128 2.064 2.064 0 0 1 0 4.128zM20.452 20.452h-3.558v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667h-3.558V8.995h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.291z"
                      fill="white"
                    />
                  </svg>
                </a>

                {/* TikTok - White Glyph */}
                <a
                  target="_blank"
                  rel="noreferrer"
                  className="hover:opacity-80 transition-opacity"
                  href="https://www.tiktok.com/@omnishare.ai"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 256 256"
                    xmlns="http://www.w3.org/2000/svg"
                    className="min-w-[20px] min-h-[20px] shrink-0"
                  >
                    <path
                      d="M120 0h40c0 22 18 40 40 40v40c-14 0-27-4-40-12v88c0 44-36 80-80 80S0 200 0 156c0-44 36-80 80-80h40v40H80c-22 0-40 18-40 40s18 40 40 40 40-18 40-40V0z"
                      fill="white"
                    />
                  </svg>
                </a>

                {/* YouTube - White Play Button with Cut-out Triangle */}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://www.youtube.com/@OmniShare-ai"
                  className="hover:opacity-80 transition-opacity"
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg
                    width="29"
                    height="29"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="min-w-[20px] min-h-[20px] shrink-0"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z M9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                      fill="white"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
          <div className="theme-text-light text-center text-sm mt-1 py-1">
            {`© ${new Date().getFullYear()} OmniShare`}
          </div>
        </div>
      </div>

      {/* Backdrop for Mobile */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden"
          onClick={handleSidebarClose}
        />
      )}

      {/* Top Header */}
      <div className="fixed top-0 z-10 w-full border-b border-white/20 md:px-4 py-4 px-3 bg-white h-[60px] safe-area-top">
        <div className="flex items-center justify-between mt-0">
          <div className="flex gap-3 items-center">
            <button
              onClick={handleSidebarOpen}
              className="rounded-md theme-text-primary hover:theme-text-secondary relative"
            >
              <Menu className="w-6 h-6" />
              {unreadCount != 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-purple-500 rounded-full flex items-center justify-center text-xs text-white font-bold">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>

          <Link to="/dashboard" className="-ml-[73px] absolute left-[50%]">
            <img src={logoText} alt="Logo" className="h-4" />
          </Link>

          <div className="flex items-center space-x-1">
            <div className="md:block hidden">
              <LanguageDropdown />
            </div>
            <div className="flex gap-x-4 items-center" ref={packageDropdownRef}>
              {showSkeleton ? (
  <CoinSkeleton />


              ) : (
                <WalletBalance
                  setShowPackage={(e: any) => {
                    e.stopPropagation();
                    setShowPackage((prev: any) => !prev);
                  }}
                  balance={(
                    user?.wallet?.coins + user?.wallet?.referralCoin
                  ).toLocaleString()}
                  showPackage={showPackage}
                />
              )}
              {showPackage && (
                <>
                  <div
                    className="top-[50px] right-0 left-0 bottom-0 absolute bg-black opacity-50 md:hidden h-screen"
                    onClick={() => setShowPackage(false)}
                  />
                  <div
                    className="z-100 left-0 right-0 ml-3 mr-3 md:mr-10 md:right-6 absolute bg-gray-50 md:left-auto top-5 mt-6 rounded-md shadow-md md:px-6 px-4 py-6 border md:w-[370px]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Package content */}
                    {user?.wallet?.package ? (
                      <>
                        {/* Plan Section */}
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start gap-2 text-slate-800 text-md font-medium">
                            <Icon name="crown" size={24} />
                            <div>
                              <div className="flex items-center gap-3">
                                <h2 className="text-base font-semibold text-slate-800">
                                  {t("my_plan")}
                                </h2>
                                <div className="relative" ref={planRef}>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      e.nativeEvent.stopImmediatePropagation();
                                      setPlanMsgOpen((prev: any) => !prev);
                                    }}
                                    className="text-slate-300 cursor-pointer text-xs"
                                  >
                                    <Icon name="question-mark" size={17} />
                                  </button>
                                  {planMsgOpen && (
                                    <div className="absolute right-[-112px] m-auto top-full mt-1 w-56 p-2 bg-gray-50 border rounded-md shadow-lg z-50 text-xs text-black">
                                      {t("tooltip_plan_part1")}{" "}
                                      <span className="font-semibold text-purple-600">
                                        {user.wallet?.package?.name || "FREE"}
                                      </span>{" "}
                                      {t("tooltip_plan_part2")}{" "}
                                      <span className="font-semibold text-purple-600">
                                        {user.wallet?.coins ?? 0}
                                      </span>{" "}
                                      {t("tooltip_plan_part3")}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <p className="text-sm text-black mt-1">
                                {user.wallet?.downgradeRequested ? (
                                  <>
                                    Downgraded to{" "}
                                    <span className="font-medium ml-1">
                                      {user.wallet?.downgradedPackage?.name}
                                    </span>{" "}
                                    <br />
                                    Effective on:
                                  </>
                                ) : user.wallet?.cancelRequested == true ? (
                                  <>
                                    Downgraded to
                                    <span className="font-medium ml-1">
                                      Free
                                    </span>
                                    <br />
                                    Effective on
                                  </>
                                ) : (
                                  t("renewing_on")
                                )}
                                &nbsp;
                                <span className="text-black font-medium">
                                  {user.wallet?.expiresAt
                                    ? new Date(
                                        user.wallet.expiresAt
                                      ).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                      })
                                    : "N/A"}
                                </span>
                              </p>
                            </div>
                          </div>

                          <span
                            className="text-base font-semibold"
                            style={{ color: "#7650e3" }}
                          >
                            {user.wallet?.package?.name || "FREE"}
                          </span>
                        </div>

                        {/* Omni Coins */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-slate-800 text-md font-medium">
                              <Icon name="spiral-logo" />
                              <div className="flex items-center gap-3">
                                <p>Omni Coins</p>
                                <div className="relative" ref={coinsRef}>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      e.nativeEvent.stopImmediatePropagation();
                                      setCoinsMsgOpen((prev: any) => !prev);
                                    }}
                                    className="text-slate-300 text-xs cursor-pointer"
                                  >
                                    <Icon name="question-mark" size={17} />
                                  </button>
                                  {coinsMsgOpen && (
                                    <div className="absolute right-[-112px] m-auto top-full mt-1 w-56 p-2 bg-gray-50 border rounded-md shadow-lg z-50 text-xs text-black">
                                      {t("tooltip_coins_part1")}{" "}
                                      <span className="font-semibold text-purple-600">
                                        {user.wallet?.coins?.toLocaleString() ??
                                          0}
                                      </span>{" "}
                                      {t("tooltip_coins_part2")}{" "}
                                      <span className="font-semibold text-purple-600">
                                        {user.wallet?.package?.coinLimit?.toLocaleString() ??
                                          0}
                                      </span>{" "}
                                      {t("tooltip_coins_part3")}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <p
                              className="text-base font-semibold"
                              style={{ color: "#7650e3" }}
                            >
                              {user.wallet.coins.toLocaleString() ?? 0}/
                              {user.wallet.package.coinLimit.toLocaleString()}
                            </p>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-md text-slate-800 font-medium">
                              <Icon name="share" className="scale-[0.8]" />
                              <div className="flex items-center gap-3">
                                <p>{t("referral_coins")}</p>
                                <div className="relative" ref={referralRef}>
                                  <button
                                    onClick={(e: any) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      e.nativeEvent.stopImmediatePropagation();
                                      setReferralMsgOpen((prev: any) => !prev);
                                    }}
                                    className="text-slate-300 text-xs cursor-pointer"
                                  >
                                    <Icon name="question-mark" size={17} />
                                  </button>
                                  {referralMsgOpen && (
                                    <div className="absolute right-[-112px] m-auto top-full mt-1 w-56 p-2 bg-gray-50 border rounded-md shadow-lg z-50 text-xs text-black">
                                      {t("tooltip_referral_part1")}{" "}
                                      <span className="font-semibold text-purple-600">
                                        {user?.wallet?.referralCoin?.toLocaleString() ??
                                          0}
                                      </span>{" "}
                                      {t("tooltip_referral_part2")}{" "}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <p
                              className="text-base font-semibold"
                              style={{ color: "#7650e3" }}
                            >
                              {user?.wallet?.referralCoin?.toLocaleString()}
                            </p>
                          </div>
                        </div>

                        <p className="text-base my-4">
                          {t("more_info")}{" "}
                          <Link
                            className="text-purple-600 font-medium"
                            to="/faq"
                          >
                            FAQs
                          </Link>
                        </p>

                        {user?.wallet?.package?.tier !== "free" && (
                          <button
                            onClick={() => openManageSubscription()}
                            className="w-full py-2.5 text-md font-semibold rounded-md border bg-white flex items-center justify-center gap-2 transition hover:bg-[#d7d7fc] hover:text-[#7650e3] hover:border-[#7650e3]"
                            style={{
                              borderColor: "#7650e3",
                              color: "#7650e3",
                            }}
                          >
                            <Icon
                              name="manage-subs"
                              size={20}
                              className="filter-omni"
                            />
                            {t("manage_subscription")}
                          </button>
                        )}

                        {user.wallet?.downgradeRequested !== null ||
                          (user.wallet?.package?.tier !== "pro" && (
                            <Link
                              to="/pricing"
                              onClick={() => setShowPackage(false)}
                              className="w-full mt-3 px-3 py-2.5 border text-md font-semibold rounded-md group flex items-center justify-center gap-2 text-white bg-[#7650e3] hover:bg-[#d7d7fc] hover:text-[#7650e3] border-[#7650e3]"
                            >
                              <div className="group-hover:filter-omni h-full w-full text-center">
                                <Icon
                                  name="white-diamond"
                                  size={20}
                                  className="mr-2"
                                />
                                {t("upgrade")}
                              </div>
                            </Link>
                          ))}
                      </>
                    ) : (
                      <p className="text-gray-500 font-medium text-md text-center">
                        No active package found
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {isLogoutModalOpen && (
        <LogoutModal
          close={() => setIsLogoutModalOpen(false)} // Close the modal
          t={t} // Translation function
          onConfirmAction={handleLogout} // Call logout on confirm
        />
      )}
    </div>
  );
};

export default Sidebar;
